import { useEffect, useState, KeyboardEvent } from 'react';
import TextField from '@mui/material/TextField';
import Autocomplete from '@mui/material/Autocomplete';
import InputAdornment from '@mui/material/InputAdornment';
import SearchIcon from '@mui/icons-material/Search';
import { useNavigate } from 'react-router';
import { SearchStudentsResult, useSearchStudents } from 'api/hooks/private/users/students/SearchStudents';
import { useDebounceValue } from 'usehooks-ts';

export function AdminSearch() {
    // open state of search box to determine when to enable the query
    const [open, setOpen] = useState(false);

    // input value for the search box
    const [inputValue, setInputValue] = useState('');

    // debounced input value to prevent excessive api calls
    const [debouncedInputValue, setDebouncedInputValue] = useDebounceValue(inputValue, 500);

    // react router hook to navigate
    const navigate = useNavigate();

    // search student query hook
    const searchResults = useSearchStudents({
        // use the debounced input value
        variables: debouncedInputValue,

        // only search when the search box is open and has input
        enabled: open && debouncedInputValue.length > 0
    })

    useEffect(() => {
        // update the debounced input value when input value changes
        setDebouncedInputValue(inputValue);
    }, [inputValue, setDebouncedInputValue]);


    const handleSelection = (value: SearchStudentsResult | null): void => {
        // if an option was selected
        if (value?.user_id) {
            // clear the input
            setInputValue('');

            // navigate to the students profile page
            navigate(`/admin/students/${value.user_id}`)
        }
    };

    // function to construct a label for each user
    const getOptionLabel = (result: SearchStudentsResult) => {
        const user = result.user;
        const label = [user.first_name];

        // if the user has a nickname add it to the label
        if (user.nickname) {
            label.push(`(${user.nickname})`);
        }

        // add the users last name
        label.push(user.last_name);

        // add inactive to end of inactive users
        if (!user.active) {
            label.push('(Inactive)');
        }

        // return the label as a string
        return label.join(' ');
    };

    // handle enter key to select the first option
    const handleKeyDown = (event: KeyboardEvent) => {
        if (event.key === 'Enter' && !searchResults.isFetching && searchResults.data?.length) {
            handleSelection(searchResults.data[0]);
            // event.preventDefault();
        }
    };

    return (
        <Autocomplete
            value={null} // don't store the selected option since we are just navigating to a page
            inputValue={inputValue}
            options={searchResults.data ?? []}
            filterOptions={(x) => x} // disable built-in filtering
            onOpen={() => setOpen(true)}
            onClose={() => setOpen(false)}
            getOptionKey={(option: SearchStudentsResult) => option.user_id}
            getOptionLabel={getOptionLabel}
            onChange={(_, value: SearchStudentsResult | null) => handleSelection(value)}
            onInputChange={(_, value) => setInputValue(value)}
            loading={searchResults.isFetching}
            blurOnSelect
            forcePopupIcon={false}
            fullWidth
            sx={{
                // hide search bar on mobile
                display: {
                    xs: 'none',
                    sm: 'block'
                },
                // width: 300,
                maxWidth: 300,

                zIndex: (theme) => theme.zIndex.modal
            }}
            renderInput={(props) => (
                <TextField
                    {...props}
                    size="small"
                    placeholder="Search..."
                    onKeyDown={handleKeyDown}
                    slotProps={{
                        input: {
                            ...props.InputProps,
                            startAdornment: (
                                <InputAdornment position="start" /*sx={{ marginRight: 0 }}*/>
                                    <SearchIcon />
                                </InputAdornment>
                            )
                        }
                    }}
                />
            )}
        />
    );
}

export default AdminSearch;